import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  VStack,
  HStack,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Grid,
  GridItem,
  Icon,
  Spinner,
  Center,
  TableContainer,
  Button,
  useToast,
  useBreakpointValue,
  Stack,
  SimpleGrid,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Tooltip,
  Progress,
  StatGroup,
  StatArrow,
  StatHelpText as StatHelpTextComponent
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaExclamationTriangle,
  FaCheckCircle, 
  FaClock, 
  FaCalendarAlt,
  FaBars,
  FaEye,
  FaSync,
  FaDownload,
  FaFilter,
  FaMoneyBillWave // Add this missing icon
} from "react-icons/fa";
import API from "../../context/API"; // Add this missing import

// Add environment check at the top
const isDevelopmentServer = process.env.REACT_APP_ADMIN_URL?.includes('dev.server.kiwikrave.com') || 
                           (process.env.REACT_APP_ADMIN_URL || 'https://dev.server.kiwikrave.com').includes('dev.server.kiwikrave.com');

// Animation variants
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: "easeOut"
    }
  })
};

const MotionBox = motion(Box);
const MotionCard = motion(Card);
const MotionGridItem = motion(GridItem);

function PaymentsAdmin() {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [clearedPayments, setClearedPayments] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPending: 0,
    totalCleared: 0,
    totalUpcoming: 0,
    totalAmount: 0
  });

  // Responsive breakpoints
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false });
  const isDesktop = useBreakpointValue({ base: false, lg: true });
  
  // Responsive values
  const containerPadding = useBreakpointValue({ base: 4, md: 6, lg: 8 });
  const headingSize = useBreakpointValue({ base: "xl", md: "2xl" });
  const statsGridCols = useBreakpointValue({ base: 2, md: 4 });
  const cardPadding = useBreakpointValue({ base: 4, md: 6 });
  const tableMinWidth = useBreakpointValue({ base: "100%", md: "600px", lg: "800px" });

  // Enhanced color scheme
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const subtleTextColor = useColorModeValue("gray.600", "gray.300");
  const accentColor = useColorModeValue("blue.500", "blue.400");
  const successColor = useColorModeValue("green.500", "green.400");
  const warningColor = useColorModeValue("orange.500", "orange.400");
  const dangerColor = useColorModeValue("red.500", "red.400");
  const hoverBg = useColorModeValue('gray.100', 'gray.700');

  const toast = useToast();

  useEffect(() => {
    // Placeholder for fetching data from APIs in the future
    const fetchPaymentsData = async () => {
      try {
        setIsLoading(true);
        
        const [pendingResponse, clearedResponse, upcomingResponse] = await Promise.all([
          API.get("payments/pending"),
          API.get("payments/cleared"), 
          API.get("payments/upcoming")
        ]);
        
        setPendingPayments(pendingResponse.data.data || []);
        setClearedPayments(clearedResponse.data.data || []);
        setUpcomingPayments(upcomingResponse.data.data || []);
        
      } catch (error) {
        console.error("Error fetching payment data:", error);
        // Only show empty arrays on production
        if (!isDevelopmentServer) {
          setPendingPayments([]);
          setClearedPayments([]);
          setUpcomingPayments([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentsData();
  }, [toast]);

  const renderNoDataMessage = () => (
    <Tr>
      <Td colSpan="4" textAlign="center" py={8}>
        <VStack spacing={3}>
          <Icon as={FaExclamationTriangle} color={subtleTextColor} size="lg" />
          <Text fontSize="lg" color={subtleTextColor}>No data to display</Text>
          <Text fontSize="sm" color={subtleTextColor}>Payment information will appear here once available</Text>
        </VStack>
      </Td>
    </Tr>
  );

  // Mobile-friendly table row component
  const MobileTableRow = ({ payment, type, index }) => (
    <Card
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      mb={3}
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="lg"
      _hover={{ shadow: "md" }}
    >
      <CardBody p={4}>
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Text fontWeight="bold" color={textColor}>Merchant</Text>
            <Text color={textColor}>{payment.merchant}</Text>
          </HStack>
          <HStack justify="space-between">
            <Text fontWeight="bold" color={textColor}>Amount</Text>
            <Text color={textColor} fontWeight="bold">${payment.amount}</Text>
          </HStack>
          <HStack justify="space-between">
            <Text fontWeight="bold" color={textColor}>
              {type === 'cleared' ? 'Payment Date' : 'Due Date'}
            </Text>
            <Text color={textColor}>
              {type === 'cleared' ? payment.paymentDate : payment.dueDate}
            </Text>
          </HStack>
          <HStack justify="space-between">
            <Text fontWeight="bold" color={textColor}>Status</Text>
            <Badge
              colorScheme={
                type === 'pending' ? 'orange' : 
                type === 'cleared' ? 'green' : 'blue'
              }
              variant="subtle"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="xs"
            >
              {type === 'pending' ? 'Pending' : 
               type === 'cleared' ? 'Cleared' : 'Scheduled'}
            </Badge>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );

  if (isLoading) {
    return (
      <Center height="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color={accentColor} thickness="4px" />
          <Text color={subtleTextColor}>Loading payment data...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box p={containerPadding} bg={bgColor} minH="100vh">
      <MotionBox
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <VStack spacing={{ base: 6, md: 8 }} align="stretch">
          {/* Enhanced Header */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <Heading 
              size={headingSize} 
              bgGradient="linear(to-r, blue.600, purple.600)"
              bgClip="text"
              textAlign="center"
              mb={2}
            >
              Payment Management
            </Heading>
            <Text 
              color={subtleTextColor} 
              textAlign="center" 
              fontSize={{ base: "md", md: "lg" }}
              px={{ base: 2, md: 0 }}
            >
              Monitor and manage all payment transactions across the platform
            </Text>
          </motion.div>

          {/* Enhanced Stats Grid - Responsive */}
          <SimpleGrid columns={statsGridCols} spacing={{ base: 4, md: 6 }}>
            <MotionCard
              custom={0}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              boxShadow="sm"
              _hover={{
                boxShadow: "lg",
                transform: "translateY(-4px)",
                transition: "all 0.3s ease"
              }}
              transition="all 0.3s ease"
            >
              <CardBody p={cardPadding}>
                <Stack spacing={4} align="center" textAlign="center">
                  <Box
                    p={3}
                    bg={warningColor}
                    color="white"
                    borderRadius="full"
                    _hover={{
                      transform: "scale(1.1) rotate(5deg)",
                      transition: "all 0.3s ease"
                    }}
                  >
                    <FaClock size={isMobile ? "20px" : "24px"} />
                  </Box>
                  <Stat>
                    <StatLabel color={subtleTextColor} fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">
                      Pending
                    </StatLabel>
                    <StatNumber color={textColor} fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold">
                      {stats.totalPending}
                    </StatNumber>
                    <StatHelpText color={warningColor} fontSize={{ base: "xs", md: "sm" }}>
                      Awaiting clearance
                    </StatHelpText>
                  </Stat>
                </Stack>
              </CardBody>
            </MotionCard>

            <MotionCard
              custom={1}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              boxShadow="sm"
              _hover={{
                boxShadow: "lg",
                transform: "translateY(-4px)",
                transition: "all 0.3s ease"
              }}
              transition="all 0.3s ease"
            >
              <CardBody p={cardPadding}>
                <Stack spacing={4} align="center" textAlign="center">
                  <Box
                    p={3}
                    bg={successColor}
                    color="white"
                    borderRadius="full"
                    _hover={{
                      transform: "scale(1.1) rotate(5deg)",
                      transition: "all 0.3s ease"
                    }}
                  >
                    <FaCheckCircle size={isMobile ? "20px" : "24px"} />
                  </Box>
                  <Stat>
                    <StatLabel color={subtleTextColor} fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">
                      Cleared
                    </StatLabel>
                    <StatNumber color={textColor} fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold">
                      {stats.totalCleared}
                    </StatNumber>
                    <StatHelpText color={successColor} fontSize={{ base: "xs", md: "sm" }}>
                      Successfully processed
                    </StatHelpText>
                  </Stat>
                </Stack>
              </CardBody>
            </MotionCard>

            <MotionCard
              custom={2}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              boxShadow="sm"
              _hover={{
                boxShadow: "lg",
                transform: "translateY(-4px)",
                transition: "all 0.3s ease"
              }}
              transition="all 0.3s ease"
            >
              <CardBody p={cardPadding}>
                <Stack spacing={4} align="center" textAlign="center">
                  <Box
                    p={3}
                    bg={accentColor}
                    color="white"
                    borderRadius="full"
                    _hover={{
                      transform: "scale(1.1) rotate(5deg)",
                      transition: "all 0.3s ease"
                    }}
                  >
                    <FaCalendarAlt size={isMobile ? "20px" : "24px"} />
                  </Box>
                  <Stat>
                    <StatLabel color={subtleTextColor} fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">
                      Upcoming
                    </StatLabel>
                    <StatNumber color={textColor} fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold">
                      {stats.totalUpcoming}
                    </StatNumber>
                    <StatHelpText color={accentColor} fontSize={{ base: "xs", md: "sm" }}>
                      Scheduled for future
                    </StatHelpText>
                  </Stat>
                </Stack>
              </CardBody>
            </MotionCard>

            <MotionCard
              custom={3}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              boxShadow="sm"
              _hover={{
                boxShadow: "lg",
                transform: "translateY(-4px)",
                transition: "all 0.3s ease"
              }}
              transition="all 0.3s ease"
            >
              <CardBody p={cardPadding}>
                <Stack spacing={4} align="center" textAlign="center">
                  <Box
                    p={3}
                    bg={dangerColor}
                    color="white"
                    borderRadius="full"
                    _hover={{
                      transform: "scale(1.1) rotate(5deg)",
                      transition: "all 0.3s ease"
                    }}
                  >
                    <FaMoneyBillWave size={isMobile ? "20px" : "24px"} />
                  </Box>
                  <Stat>
                    <StatLabel color={subtleTextColor} fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">
                      Total Amount
                    </StatLabel>
                    <StatNumber color={textColor} fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold">
                      ${stats.totalAmount.toFixed(2)}
                    </StatNumber>
                    <StatHelpText color={dangerColor} fontSize={{ base: "xs", md: "sm" }}>
                      All transactions
                    </StatHelpText>
                  </Stat>
                </Stack>
              </CardBody>
            </MotionCard>
          </SimpleGrid>

          {/* Enhanced Payment Tables - Responsive */}
          <VStack spacing={{ base: 4, md: 6 }} align="stretch">
            {/* Pending Payments */}
            <MotionCard
              variants={cardVariants}
              custom={4}
              initial="hidden"
              animate="visible"
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              boxShadow="sm"
              overflow="hidden"
            >
              <CardBody p={0}>
                <Box p={{ base: 4, md: 6 }} borderBottom="1px solid" borderColor={borderColor}>
                  <Stack direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "flex-start", sm: "center" }} spacing={3}>
                    <HStack spacing={3}>
                      <Icon as={FaClock} color={warningColor} />
                      <Heading size={{ base: "md", md: "lg" }} color={textColor}>Pending Payments</Heading>
                    </HStack>
                    <Badge colorScheme="orange" variant="subtle" px={3} py={1} borderRadius="full">
                      {pendingPayments.length} payments
                    </Badge>
                  </Stack>
                </Box>
                
                {/* Mobile View */}
                {isMobile ? (
                  <Box p={4}>
                    {pendingPayments && pendingPayments.length > 0 ? (
                      pendingPayments.map((payment, index) => (
                        <MobileTableRow key={index} payment={payment} type="pending" index={index} />
                      ))
                    ) : (
                      <Box textAlign="center" py={8}>
                        <Icon as={FaExclamationTriangle} color={subtleTextColor} size="lg" mb={3} />
                        <Text fontSize="lg" color={subtleTextColor}>No pending payments</Text>
                      </Box>
                    )}
                  </Box>
                ) : (
                  /* Desktop View */
                  <TableContainer>
                    <Table variant="simple" minW={tableMinWidth}>
                      <Thead>
                        <Tr bg={hoverBg}>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Merchant</Th>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Amount</Th>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Due Date</Th>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {pendingPayments && pendingPayments.length > 0 ? (
                          pendingPayments.map((payment, index) => (
                            <Tr
                              key={index}
                              as={motion.tr}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.1 }}
                              _hover={{ bg: hoverBg }}
                              cursor="pointer"
                            >
                              <Td color={textColor} fontWeight="medium">{payment.merchant}</Td>
                              <Td color={textColor} fontWeight="bold">${payment.amount}</Td>
                              <Td color={textColor}>{payment.dueDate}</Td>
                              <Td>
                                <Badge colorScheme="orange" variant="subtle" px={3} py={1} borderRadius="full">
                                  Pending
                                </Badge>
                              </Td>
                            </Tr>
                          ))
                        ) : (
                          renderNoDataMessage()
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </CardBody>
            </MotionCard>

            {/* Cleared Payments */}
            <MotionCard
              variants={cardVariants}
              custom={5}
              initial="hidden"
              animate="visible"
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              boxShadow="sm"
              overflow="hidden"
            >
              <CardBody p={0}>
                <Box p={{ base: 4, md: 6 }} borderBottom="1px solid" borderColor={borderColor}>
                  <Stack direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "flex-start", sm: "center" }} spacing={3}>
                    <HStack spacing={3}>
                      <Icon as={FaCheckCircle} color={successColor} />
                      <Heading size={{ base: "md", md: "lg" }} color={textColor}>Cleared Payments</Heading>
                    </HStack>
                    <Badge colorScheme="green" variant="subtle" px={3} py={1} borderRadius="full">
                      {clearedPayments.length} payments
                    </Badge>
                  </Stack>
                </Box>
                
                {/* Mobile View */}
                {isMobile ? (
                  <Box p={4}>
                    {clearedPayments && clearedPayments.length > 0 ? (
                      clearedPayments.map((payment, index) => (
                        <MobileTableRow key={index} payment={payment} type="cleared" index={index} />
                      ))
                    ) : (
                      <Box textAlign="center" py={8}>
                        <Icon as={FaExclamationTriangle} color={subtleTextColor} size="lg" mb={3} />
                        <Text fontSize="lg" color={subtleTextColor}>No cleared payments</Text>
                      </Box>
                    )}
                  </Box>
                ) : (
                  /* Desktop View */
                  <TableContainer>
                    <Table variant="simple" minW={tableMinWidth}>
                      <Thead>
                        <Tr bg={hoverBg}>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Merchant</Th>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Amount</Th>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Payment Date</Th>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {clearedPayments && clearedPayments.length > 0 ? (
                          clearedPayments.map((payment, index) => (
                            <Tr
                              key={index}
                              as={motion.tr}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.1 }}
                              _hover={{ bg: hoverBg }}
                              cursor="pointer"
                            >
                              <Td color={textColor} fontWeight="medium">{payment.merchant}</Td>
                              <Td color={textColor} fontWeight="bold">${payment.amount}</Td>
                              <Td color={textColor}>{payment.paymentDate}</Td>
                              <Td>
                                <Badge colorScheme="green" variant="subtle" px={3} py={1} borderRadius="full">
                                  Cleared
                                </Badge>
                              </Td>
                            </Tr>
                          ))
                        ) : (
                          renderNoDataMessage()
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </CardBody>
            </MotionCard>

            {/* Upcoming Payments */}
            <MotionCard
              variants={cardVariants}
              custom={6}
              initial="hidden"
              animate="visible"
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              boxShadow="sm"
              overflow="hidden"
            >
              <CardBody p={0}>
                <Box p={{ base: 4, md: 6 }} borderBottom="1px solid" borderColor={borderColor}>
                  <Stack direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "flex-start", sm: "center" }} spacing={3}>
                    <HStack spacing={3}>
                      <Icon as={FaCalendarAlt} color={accentColor} />
                      <Heading size={{ base: "md", md: "lg" }} color={textColor}>Upcoming Payments</Heading>
                    </HStack>
                    <Badge colorScheme="blue" variant="subtle" px={3} py={1} borderRadius="full">
                      {upcomingPayments.length} payments
                    </Badge>
                  </Stack>
                </Box>
                
                {/* Mobile View */}
                {isMobile ? (
                  <Box p={4}>
                    {upcomingPayments && upcomingPayments.length > 0 ? (
                      upcomingPayments.map((payment, index) => (
                        <MobileTableRow key={index} payment={payment} type="upcoming" index={index} />
                      ))
                    ) : (
                      <Box textAlign="center" py={8}>
                        <Icon as={FaExclamationTriangle} color={subtleTextColor} size="lg" mb={3} />
                        <Text fontSize="lg" color={subtleTextColor}>No upcoming payments</Text>
                      </Box>
                    )}
                  </Box>
                ) : (
                  /* Desktop View */
                  <TableContainer>
                    <Table variant="simple" minW={tableMinWidth}>
                      <Thead>
                        <Tr bg={hoverBg}>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Merchant</Th>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Amount</Th>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Due Date</Th>
                          <Th color={textColor} fontSize="sm" fontWeight="bold">Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {upcomingPayments && upcomingPayments.length > 0 ? (
                          upcomingPayments.map((payment, index) => (
                            <Tr
                              key={index}
                              as={motion.tr}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.1 }}
                              _hover={{ bg: hoverBg }}
                              cursor="pointer"
                            >
                              <Td color={textColor} fontWeight="medium">{payment.merchant}</Td>
                              <Td color={textColor} fontWeight="bold">${payment.amount}</Td>
                              <Td color={textColor}>{payment.dueDate}</Td>
                              <Td>
                                <Badge colorScheme="blue" variant="subtle" px={3} py={1} borderRadius="full">
                                  Scheduled
                                </Badge>
                              </Td>
                            </Tr>
                          ))
                        ) : (
                          renderNoDataMessage()
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </CardBody>
            </MotionCard>
          </VStack>
        </VStack>
      </MotionBox>
    </Box>
  );
}

export default PaymentsAdmin;
